// booking.jsx  —  TheVisa · Country Visa Detail Page
import { useState, useEffect, useRef, useCallback } from "react";
import { saveApplication } from "../Users/ProfileLogic.js";
import {
  getVisaData, STEP_ICONS, AVATAR_COLORS, TIER_CONFIG,
  REVIEWS_INITIAL, FAQS_INITIAL,
  VISA_TYPES, REFUND_POLICIES, APPROVAL_RATES, CURRENCY_CONFIG, inrToLocal,
  GOV_FEE_INCREASE_PCT, GOV_FEE_PAGE_LINKS, REFUND_JOURNEY_STEP,
  buildRefundStepDesc,
} from "./bookingdata";
import "./Booking.css";
import { useParams, useNavigate } from "react-router-dom";
import Login from "../Users/Login";
import { useAuth } from "../../../context/AuthContext";
import VisaService from "../../../services/visaService";


// ─── LOGIN GATE MODAL ────────────────────────────────────────────────────
function LoginGate({ open, onClose, onSuccess }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  function handleLoginSuccess(user) {
    onSuccess(user);
    onClose();
  }

  return (
    <div
      className="bk-lg-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bk-lg-inner">
        <button className="bk-lg-close" onClick={onClose}>✕</button>
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    </div>
  );
}


// ─── useLoginGate HOOK ────────────────────────────────────────────────────
function useLoginGate() {
  const { user: authUser } = useAuth();
  const [gateOpen, setGateOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(authUser);

  const requireLogin = useCallback((action) => {
    if (authUser) {
      action(authUser);
    } else {
      setPendingAction(() => action);
      setGateOpen(true);
    }
  }, []);

  const handleLoginSuccess = useCallback((user) => {
    setLoggedInUser(user);
    setGateOpen(false);
    if (pendingAction) {
      pendingAction(user);
      setPendingAction(null);
    }
  }, [pendingAction]);

  const closeGate = useCallback(() => {
    setGateOpen(false);
    setPendingAction(null);
  }, []);

  return { gateOpen, loggedInUser, requireLogin, handleLoginSuccess, closeGate };
}


// ─── PLANE SVG ────────────────────────────────────────────────────────────
function PlaneSVG() {
  return (
    <svg
        fill="#0b0762"
        viewBox="0 0 46 25"
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        className="bk-plane-svg"
      >
      <title>plane-solid</title>

      <path
        d="M6.25,11.5,12,13.16l6.32-4.59-9.07.26A.52.52,0,0,0,9,8.91L6.13,10.56A.51.51,0,0,0,6.25,11.5Z"
      />
      <path
        d="M34.52,6.36,28.22,5a3.78,3.78,0,0,0-3.07.67L6.12,19.5l-4.57-.2a1.25,1.25,0,0,0-.83,2.22l4.45,3.53a.55.55,0,0,0,.53.09c1.27-.49,6-3,11.59-6.07l1.12,11.51a.55.55,0,0,0,.9.37l2.5-2.08a.76.76,0,0,0,.26-.45l2.37-13.29c4-2.22,7.82-4.37,10.51-5.89A1.55,1.55,0,0,0,34.52,6.36Z"
      />

      <rect x="0" y="0" width="36" height="36" fillOpacity="0" />
    </svg>
  );
}

// ─── RUNWAY PROCESS COMPONENT ─────────────────────────────────────────────
function RunwayProcess({ steps }) {
  const trackRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [planeLeft, setPlaneLeft] = useState(0);
  const [progWidth, setProgWidth] = useState(0);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startLeft = useRef(0);

  const getPositions = useCallback(() => {
    if (!trackRef.current) return [];
    const w = trackRef.current.offsetWidth;
    return steps.map((_, i) => Math.round((i / (steps.length - 1)) * w));
  }, [steps]);

  const nearestStep = useCallback((x) => {
    const pos = getPositions();
    const w = trackRef.current?.offsetWidth ?? 0;
    x = Math.max(0, Math.min(w, x));
    return pos.reduce((best, p, i) => (Math.abs(x - p) < Math.abs(x - pos[best]) ? i : best), 0);
  }, [getPositions]);

  const snapToStep = useCallback((step) => {
    const pos = getPositions();
    if (!pos.length) return;
    setPlaneLeft(pos[step] - 30);
    setProgWidth(pos[step]);
    setActiveStep(step);
  }, [getPositions]);

  useEffect(() => { const t = setTimeout(() => snapToStep(0), 50); return () => clearTimeout(t); }, [snapToStep]);
  useEffect(() => {
    const onResize = () => snapToStep(activeStep);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [activeStep, snapToStep]);

  useEffect(() => {
    const onMove = (cx) => {
      if (!dragging.current || !trackRef.current) return;
      const w = trackRef.current.offsetWidth;
      const nl = Math.max(-30, Math.min(w - 30, startLeft.current + cx - startX.current));
      setPlaneLeft(nl);
      setProgWidth(Math.max(0, Math.min(w, nl + 30)));
      setActiveStep(nearestStep(nl + 30));
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setPlaneLeft((prev) => { const s = nearestStep(prev + 30); snapToStep(s); return prev; });
    };
    window.addEventListener("mousemove", (e) => onMove(e.clientX));
    window.addEventListener("touchmove", (e) => onMove(e.touches[0].clientX), { passive: true });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", (e) => onMove(e.clientX));
      window.removeEventListener("touchmove", (e) => onMove(e.touches[0].clientX));
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [nearestStep, snapToStep]);

  const positions = getPositions();
  const onMouseDown = (e) => { dragging.current = true; startX.current = e.clientX; startLeft.current = planeLeft; };
  const onTouchStart = (e) => { dragging.current = true; startX.current = e.touches[0].clientX; startLeft.current = planeLeft; };

  return (
    <div className="bk-rw-process">
      {/* <div className="bk-rw-labels">
        {steps.map((step, i) => (
          <div key={i} className="bk-rw-lbl" style={{ left: positions[i] ?? 0 }} onClick={() => snapToStep(i)}>
            <div className={["bk-rw-lbl__icon", i < activeStep ? "passed" : "", i === activeStep ? "active" : ""].filter(Boolean).join(" ")}>
              {STEP_ICONS[i] ?? STEP_ICONS[0]}
            </div>
            <div className="bk-rw-lbl__title">{step.title}</div>
          </div>
        ))}
      </div> */}
      <div className="bk-rw-track" ref={trackRef}>
        <div className="bk-rw-prog" style={{ width: progWidth }} />
        <div className="bk-rw-dash" />
        {positions.map((pos, i) => (
          <div key={i} className="bk-rw-cp" style={{ left: pos }} onClick={() => snapToStep(i)}>
            <div className={`bk-rw-cp__bar${i <= activeStep ? " passed" : ""}`} />
            <div className={["bk-rw-cp__dot", i < activeStep ? "passed" : "", i === activeStep ? "active" : ""].filter(Boolean).join(" ")} />
          </div>
        ))}
        <div className="bk-rw-plane" style={{ left: planeLeft, transition: dragging.current ? "none" : "left 0.4s cubic-bezier(0.25,0.8,0.25,1)" }} onMouseDown={onMouseDown} onTouchStart={onTouchStart}>
          <PlaneSVG />
        </div>
      </div>
      <div className="bk-rw-nums">
        {positions.map((pos, i) => (
          <div key={i} className="bk-rw-num-wrap" style={{ left: pos }} onClick={() => snapToStep(i)}>
            <div className={`bk-rw-num${i <= activeStep ? " passed" : ""}`}>{i + 1}</div>
          </div>
        ))}
      </div>
      <div className="bk-rw-info">
        <div className="bk-rw-info__icon">{STEP_ICONS[activeStep] ?? STEP_ICONS[0]}</div>
        <div className="bk-rw-info__body">
          <span className="bk-rw-info__tag">Step {activeStep + 1} of {steps.length}</span>
          <div className="bk-rw-info__title">{steps[activeStep]?.title}</div>
          <div className="bk-rw-info__desc">{steps[activeStep]?.desc}</div>
        </div>
      </div>
    </div>
  );
}


function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function Stars({ n }) { return <span>{"★".repeat(n)}{"☆".repeat(5 - n)}</span>; }
function SectionHead({ label, title }) {
  return (
    <div className="bk-section__head">
      <div className="bk-section__label">{label}</div>
      <h2 className="bk-section__title">{title}</h2>
    </div>
  );
}

// ─── FAQ ITEM ─────────────────────────────────────────────────────────────
function FaqItem({ q, a, isLast }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bk-faq ${open ? "open" : ""} ${isLast ? "bk-faq--last" : ""}`}>
      <button className="bk-faq__q" onClick={() => setOpen(!open)}>
        <span className="bk-faq__q-text">{q}</span>
        <span className="bk-faq__chevron">{open ? "−" : "+"}</span>
      </button>
      <div className="bk-faq__body">
        <div className="bk-faq__body-inner">
          <div className="bk-faq__answer">{a}</div>
        </div>
      </div>
    </div>
  );
}

// ─── DESTINATION GRID ─────────────────────────────────────────────────────
function DestGrid({ items }) {
  const top = items.slice(0, 2);
  const bottom = items.slice(2, 6);
  return (
    <div className="bk-dest-grid">
      {top.map((d, i) => (
        <div key={d.name} className={`bk-dest-card${i === 0 ? " bk-dest-card--wide" : ""}`}>
          <img src={d.img} alt={d.name} className="bk-dest-card__img" loading="lazy" />
          <div className="bk-dest-card__overlay" />
          <span className="bk-dest-card__name">{d.name}</span>
        </div>
      ))}
      {bottom.map((d) => (
        <div key={d.name} className="bk-dest-card">
          <img src={d.img} alt={d.name} className="bk-dest-card__img" loading="lazy" />
          <div className="bk-dest-card__overlay" />
          <span className="bk-dest-card__name">{d.name}</span>
        </div>
      ))}
    </div>
  );
}


// ─── REWARDS MODAL ────────────────────────────────────────────────────────
function RewardsModal({ open, onClose, onApply }) {
  const [openTier, setOpenTier] = useState(null);
  const [becomeOpen, setBecomeOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleOverlay = (e) => { if (e.target === e.currentTarget) onClose(); };
  const toggleTier = (id) => setOpenTier((prev) => (prev === id ? null : id));

  const handleApply = () => { onClose(); onApply(); };

  return (
    <div className={`bk-modal-overlay ${open ? "open" : ""}`} onClick={handleOverlay}>
      <div className="bk-modal">

        <div className="bk-modal__head">
          <button className="bk-modal__close" onClick={onClose}>✕</button>
          <div className="bk-modal__title">Earn as you travel</div>
          <div className="bk-modal__sub">Every application brings you closer to exclusive benefits. All rewards apply on visa application fees charged.</div>
        </div>

        <div className="bk-modal__body">

          <div>
            <div className="bk-modal__section-label">Welcome Bonus</div>
            <div className="bk-modal__bonus">
              <div>
                <div className="bk-modal__bonus-title">Joining reward</div>
                <div className="bk-modal__bonus-desc">Join us and unlock your welcome reward instantly</div>
              </div>
              <div className="bk-modal__bonus-amount">
                <span className="bk-currency">₹</span>
                100
              </div>
            </div>
          </div>

          <div>
            <div className="bk-modal__section-label">Referral &amp; Discount Codes</div>
            <div className="bk-modal__codes">
              <div className="bk-modal__code-row">
                <div className="bk-modal__code-pill">MEMBERCODE</div>
                <div>
                  <div className="bk-modal__code-title">Member code</div>
                  <div className="bk-modal__code-desc">Use the member code — you'll receive a discount on the visa fee.</div>
                </div>
              </div>
              <div className="bk-modal__code-row">
                <div className="bk-modal__code-pill">AGENTCODE</div>
                <div>
                  <div className="bk-modal__code-title">Agent code</div>
                  <div className="bk-modal__code-desc">Agents receive a dedicated code that can be applied to applications, helping users get an extra discount.</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="bk-modal__tiers-title">Three-Tier Membership</div>
            <div className="bk-modal__tiers">
              {TIER_CONFIG.map((tier) => (
                <div key={tier.id} className={`bk-modal__tier ${openTier === tier.id ? "open" : ""}`} style={{ "--tier-stripe": tier.stripeColor }}>
                  <div className="bk-modal__tier-head" onClick={() => toggleTier(tier.id)}>
                    <div className="bk-modal__tier-stripe" />
                    <div className="bk-modal__tier-info">
                      <div className="bk-modal__tier-name">{tier.name}</div>
                      <div className="bk-modal__tier-desc">{tier.desc}</div>
                    </div>
                    <span className="bk-modal__tier-arrow">&#9658;</span>
                  </div>
                  <div className="bk-modal__tier-body">
                    <div className="bk-modal__tier-body-inner">
                      <div className="bk-modal__tier-perks">
                        {tier.perks.map((perk, i) => (
                          <div key={i} className="bk-modal__tier-perk">
                            <div className="bk-modal__tier-perk-dot" />
                            <div className="bk-modal__tier-perk-text">{perk}</div>
                          </div>
                        ))}
                        {tier.hasBecome && (
                          <>
                            <button
                              className="bk-modal__become-btn"
                              onClick={(e) => { e.stopPropagation(); setBecomeOpen((v) => !v); }}
                            >
                              {becomeOpen ? "Hide Details" : "*Become a Member"}
                            </button>
                            <div className={`bk-modal__become-panel ${becomeOpen ? "open" : ""}`}>
                              <div className="bk-modal__become-panel-inner">
                                <div className="bk-modal__become-content">
                                  <div className="bk-modal__become-title">Membership is closer than you think</div>
                                  <div className="bk-modal__become-desc">
                                    Become a member on your 2nd application — even a transit visa counts. Applying for family or friends also qualifies. One more application is all it takes.
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bk-modal__ms-title">Milestone Benefits</div>
            <div className="bk-modal__milestone-card">
              <div className="bk-modal__milestone-heading">Achieve milestones</div>
              <div className="bk-modal__milestone-desc">
                The more you apply — for yourself, family, or clients — the more milestone rewards you unlock.
              </div>
            </div>
          </div>

          <button className="bk-modal__cta bk-text-white" onClick={handleApply}>
            <span className="bk-text-white">Start Application</span>
          </button>

          <div className="bk-modal__tc">
            T&amp;C apply. All rewards and discounts are applicable only on the visa application fees charged by TheVisaCo.
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── SIDEBAR FEE INFO TOOLTIP ─────────────────────────────────────────────
function BkFeeInfoTooltip({ svcFee }) {
  const base = Math.round(svcFee / 1.18);
  const gst = svcFee - base;
  return (
    <div className="bk-fee-info-wrap">
      <span className="bk-fee-info-icon">i</span>
      <div className="bk-fee-tooltip">
        <div className="bk-fee-tooltip-row">
          <span>Base fee</span>
          <span>₹{base.toLocaleString("en-IN")}</span>
        </div>
        <div className="bk-fee-tooltip-row">
          <span>GST (18%)</span>
          <span>₹{gst.toLocaleString("en-IN")}</span>
        </div>
        <div className="bk-fee-tooltip-row bk-fee-tooltip-row--total">
          <span>Total</span>
          <span>₹{svcFee.toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

// ─── SEARCH BAR HELPER ────────────────────────────────────────────────────
function SearchBar({ value, onChange, placeholder }) {
  return (
    <div className="bk-rev-search">
      <svg className="bk-rev-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="7" /><path d="m21 21-4.35-4.35" />
      </svg>
      <input className="bk-rev-search__input" type="text" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      {value && <button className="bk-rev-search__clear" onClick={() => onChange("")}>✕</button>}
    </div>
  );
}

// ─── VIEW MORE BUTTON ─────────────────────────────────────────────────────
function ViewMoreBtn({ expanded, onToggle, moreCount }) {
  return (
    <div className="bk-view-more">
      <button className="bk-view-more__btn" onClick={onToggle}>
        {expanded ? (
          <>
            <span>Show Less</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </>
        ) : (
          <>
            <span>View More{moreCount > 0 ? ` (${moreCount} more)` : ""}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </>
        )}
      </button>
    </div>
  );
}

// ─── FLOATING ACTION TOGGLE ───────────────────────────────────────────────
function FloatingToggle({ onApply, onRewards, onDownloadPdf }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  const handleApply = () => { setOpen(false); onApply(); };
  const handleRewards = () => { setOpen(false); onRewards(); };
  const handlePdf = () => { setOpen(false); onDownloadPdf(); };

  return (
    <div className={`bk-fab-toggle${open ? " open" : ""}`} ref={ref}>
      <div className="bk-fab-actions">
        {/* <button className="bk-fab-action bk-fab-action--pdf" onClick={handlePdf} tabIndex={open ? 0 : -1} aria-hidden={!open}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
          </svg>
          <span>Download PDF</span>
        </button> */}
        <button className="bk-fab-action bk-fab-action--rewards" onClick={handleRewards} tabIndex={open ? 0 : -1} aria-hidden={!open}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#FFD700" stroke="#C9A000" strokeWidth="2" />
            <circle cx="12" cy="12" r="6" fill="#FFEC8B" stroke="#C9A000" strokeWidth="1.5" />
            <ellipse cx="9" cy="9" rx="3" ry="2" fill="white" opacity="0.3" />
          </svg>
          <span>Rewards</span>
        </button>
        <button className="bk-fab-action bk-fab-action--apply" onClick={handleApply} tabIndex={open ? 0 : -1} aria-hidden={!open}>
          <span className="bk-fab-action__dot" />
          <span>Apply Now</span>
        </button>
      </div>
      <button className="bk-fab-main" onClick={() => setOpen((v) => !v)} aria-label={open ? "Close menu" : "Open actions"} aria-expanded={open}>
        <span className="bk-fab-main__icon" aria-hidden="true">
          {open ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" fill="#FFD700" stroke="#C9A000" strokeWidth="2" />
              <circle cx="12" cy="12" r="6" fill="#FFEC8B" stroke="#C9A000" strokeWidth="1.5" />
              <ellipse cx="9" cy="9" rx="3" ry="2" fill="white" opacity="0.3" />
            </svg>
          )}
        </span>
        <span className="bk-fab-main__label bk-text-white">{open ? "Close" : "Rewards"}</span>
      </button>
    </div>
  );
}

// ─── INFO CARD VISA TYPE DROPDOWN ─────────────────────────────────────────
// This replaces the label in the "Visa Type" info card with an inline dropdown
function VisaTypeCardDropdown({ visaTypes, selectedType, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  // If no types or only 1, render as plain text
  if (!visaTypes || visaTypes.length <= 1) {
    return (
      <div className="bk-info-card__label bk-info-card__label--plain">
        {selectedType?.label ?? "—"}
      </div>
    );
  }

  return (
    <div className="bk-info-type-wrap" ref={ref}>
      <button
        className={`bk-info-type-trigger${open ? " open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title="Click to change visa category"
      >
        <span className="bk-info-type-trigger__text">{selectedType?.label ?? "Select visa type"}</span>
        <svg
          className={`bk-info-type-chevron${open ? " open" : ""}`}
          width="12" height="12"
          viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="bk-info-type-dropdown">
          {visaTypes.map((vt) => (
            <button
              key={vt.id}
              className={`bk-info-type-option${selectedType?.id === vt.id ? " selected" : ""}`}
              onClick={() => { onChange(vt); setOpen(false); }}
            >
              <div className="bk-info-type-option__label">{vt.label}</div>
              <div className="bk-info-type-option__meta">
                Stay: {vt.stay} &nbsp;·&nbsp; {vt.entry} entry
                {vt.govFeeMultiplier !== 1 && (
                  <span className={`bk-info-type-option__badge ${vt.govFeeMultiplier > 1 ? "higher" : "lower"}`}>
                    {vt.govFeeMultiplier > 1 ? "Higher fee" : "Lower fee"}
                  </span>
                )}
              </div>
              {vt.desc && <div className="bk-info-type-option__desc">{vt.desc}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── PDF DOWNLOAD HELPER ──────────────────────────────────────────────────
async function downloadBookingPdf(data, selectedType, computedGovFee, computedSvcFee, computedTotal, expressVisa, computedExpressFee) {
  const { jsPDF } = await import("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js").catch(() => {
    // fallback: use window print
    window.print();
    return null;
  });

  if (!jsPDF) return;

  try {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = 210;
    const margin = 18;
    let y = 20;

    // ── Header
    doc.setFillColor(14, 30, 74);
    doc.rect(0, 0, pageW, 38, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("TheVisa", margin, 18);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(180, 200, 255);
    doc.text("Official Visa Application Summary", margin, 26);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}`, pageW - margin, 26, { align: "right" });

    y = 50;

    // ── Country block
    doc.setTextColor(14, 30, 74);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text(`${data.flag ?? ""} ${data.country} Visa`, margin, y);
    y += 7;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 130);
    doc.text(data.tagline ?? "", margin, y);
    y += 10;

    // ── Divider
    doc.setDrawColor(220, 220, 235);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    // ── Visa details table
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(14, 30, 74);
    doc.text("Visa Details", margin, y);
    y += 7;

    const details = [
      ["Visa Category", selectedType?.label ?? data.visaCard.type],
      ["Stay Duration", selectedType?.stay ?? data.visaCard.stay],
      ["Validity", selectedType?.validity ?? data.visaCard.validity],
      ["Entry Type", selectedType?.entry ?? data.visaCard.entry],
      ["Processing Guarantee", data.visaCard.guarantee],
      ["Approval Rate", `${data.approvalRate ?? 95}%`],
    ];

    doc.setFontSize(10);
    details.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(60, 60, 80);
      doc.text(label + ":", margin, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(20, 20, 40);
      doc.text(String(value), margin + 55, y);
      y += 6.5;
    });

    y += 4;
    doc.setDrawColor(220, 220, 235);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    // ── Fee breakdown
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(14, 30, 74);
    doc.text("Fee Breakdown", margin, y);
    y += 7;

    const fees = [
      ["Government Fee", `₹${computedGovFee.toLocaleString("en-IN")}`, false],
      ["Service Fee (incl. GST)", `₹${computedSvcFee.toLocaleString("en-IN")}`, false],
    ];
    if (expressVisa) {
      fees.push(["Express Processing", `₹${computedExpressFee.toLocaleString("en-IN")}`, false]);
    }
    fees.push(["TOTAL", `₹${computedTotal.toLocaleString("en-IN")}`, true]);

    doc.setFontSize(10);
    fees.forEach(([label, value, isTotal]) => {
      if (isTotal) {
        doc.setFillColor(232, 237, 248);
        doc.roundedRect(margin - 2, y - 5, pageW - margin * 2 + 4, 8, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setTextColor(14, 30, 74);
      } else {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(60, 60, 80);
      }
      doc.text(label, margin + 2, y);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(14, 30, 74);
      doc.text(value, pageW - margin - 2, y, { align: "right" });
      y += 7.5;
    });

    y += 4;
    doc.setDrawColor(220, 220, 235);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    // ── Documents required
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(14, 30, 74);
    doc.text("Documents Required", margin, y);
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(60, 60, 80);
    (data.essentials ?? []).forEach((ess) => {
      doc.setFont("helvetica", "bold");
      doc.text(`• ${ess.title}`, margin + 2, y);
      doc.setFont("helvetica", "normal");
      doc.text(`  ${ess.desc}`, margin + 8, y + 4.5);
      y += 10;
    });

    y += 2;
    doc.setDrawColor(220, 220, 235);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    // ── Refund policy note
    const refundNote = data.refundPolicy?.note;
    if (refundNote) {
      doc.setFillColor(236, 253, 245);
      doc.roundedRect(margin - 2, y - 4, pageW - margin * 2 + 4, 16, 2, 2, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(6, 95, 70);
      doc.text("🛡 Refund Policy", margin + 2, y + 2);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      const splitNote = doc.splitTextToSize(refundNote, pageW - margin * 2 - 6);
      doc.text(splitNote, margin + 2, y + 8);
      y += 20 + (splitNote.length - 1) * 4;
    }

    // ── Footer
    doc.setFillColor(14, 30, 74);
    doc.rect(0, 285, pageW, 12, "F");
    doc.setTextColor(180, 200, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("TheVisa · Trusted by 50,000+ Indians · thevisa.co", pageW / 2, 292, { align: "center" });

    doc.save(`TheVisa_${data.country.replace(/\s+/g, "_")}_Application.pdf`);
  } catch (err) {
    console.error("PDF generation failed:", err);
    window.print();
  }
}


// ─── GOV FEE TOOLTIP ─────────────────────────────────────────────────────
function GovFeeTooltip({ inrAmount, visaId, govFeeIncreasePct, govFeePageLink }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const local = inrToLocal(inrAmount, visaId);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  if (!local || local.amount === 0) return <span className="bk-sb__fee-amount">FREE</span>;

  return (
    <span
      className={`bk-govfee-wrap${open ? " bk-govfee-wrap--open" : ""}`}
      ref={ref}
      onClick={() => setOpen((v) => !v)}
    >
      <span className="bk-govfee-inr">₹{inrAmount.toLocaleString("en-IN")}</span>
      <span className="bk-govfee-tooltip">
        <span className="bk-govfee-tooltip-local">
          {local.symbol}{" "}
          {typeof local.amount === "number" && local.amount % 1 === 0
            ? local.amount.toLocaleString()
            : local.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          &nbsp;{local.code}
        </span>
        <span className="bk-govfee-tooltip-sub">{local.name}</span>
        {govFeePageLink && (
          <a
            href={govFeePageLink}
            className="bk-govfee-tooltip-link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            Govt-Fee
          </a>
        )}
      </span>
    </span>
  );
}

function VisaProtectTooltip() {
  return (
    <span className="bk-protect-wrap">
      <span className="bk-protect-info-icon">i</span>
      <span className="bk-protect-tooltip">
        <span className="bk-protect-tooltip__head">
          <span>
            <div className="bk-protect-tooltip__title">Visa Protect</div>
            <div className="bk-protect-tooltip__sub">Your safety net</div>
          </span>
        </span>
        <span className="bk-protect-tooltip__body">
          <span className="bk-protect-tooltip__item">
            <span className="bk-protect-tooltip__dot" />
            <span className="bk-protect-tooltip__text">Protection Fee</span>
          </span>
          <span className="bk-protect-tooltip__item">
            <span className="bk-protect-tooltip__dot" />
            <span className="bk-protect-tooltip__text">The Fee can be Waived also.</span>
          </span>
        </span>
      </span>
    </span>
  );
}


// ─── REFUND BADGE ─────────────────────────────────────────────────────────
// Visa Protect is now handled in Profile payment - no toggle here
function RefundBadge({ refundPolicy }) {
  return (
    <div className="bk-refund-wrap">
      <div className="bk-refund-header">
        <span className="bk-refund-header__icon">🛡️</span>
        <span className="bk-refund-header__text">Protection</span>
      </div>
      <div className="bk-refund-note">
        Visa Protect will be available at payment in Profile
      </div>
    </div>
  );
}


// ─── SIDEBAR ──────────────────────────────────────────────────────────────
// VisaTypeSelector REMOVED from sidebar top — now lives in info cards
function Sidebar({
  data, visaId, onApply,
  selectedType,
  refundPolicy,
  computedGovFee, computedSvcFee, computedSvcOrig,
  computedTotal,
  expressVisa, onToggleExpress, computedExpressFee, EXPRESS_FEE,
}) {
  const stayLabel     = selectedType?.stay     ?? data.visaCard.stay;
  const validityLabel = selectedType?.validity  ?? data.visaCard.validity;
  const entryLabel    = selectedType?.entry     ?? data.visaCard.entry;

  return (
    <div className="bk-sb">
      <div className="bk-sb__topbar">
        <div className="bk-sb__guarantee"><span>{data.visaCard.guarantee}</span></div>
        <span className="bk-sb__faster">{data.visaCard.badge ?? ""}</span>
      </div>

      <div className="bk-sb__body">

        {/* Type chips — reflect current selection */}
        {/*}
        {selectedType && (
          <div className="bk-sb__type-chips">
            <span className="bk-sb__type-chip">🕑 {stayLabel}</span>
            <span className="bk-sb__type-chip">📅 {validityLabel}</span>
            <span className="bk-sb__type-chip">✈️ {entryLabel}</span>
          </div>
        )} */}

        <div className="bk-sb__price-wrap">
          <div className="bk-sb__price-main">
            ₹{computedTotal.toLocaleString("en-IN")}
          </div>
          <div className="bk-sb__price-sub">Total per person</div>
          <div className="bk-sb__price-note">
            {expressVisa ? "Govt fee + service fee + protection + express" : "Govt fee + service fee + protection included"}
          </div>
        </div>

        <button className="bk-sb__cta" onClick={onApply}>
          <span className="bk-text-white">Start Application</span>
        </button>

        <div className="bk-sb__fees">

          {/* Govt fee */}
          <div className="bk-sb__fee-row">
            <div className="bk-sb__fee-col">
              <div className="bk-sb__fee-name">Govt Fee</div>
              <div className="bk-sb__fee-note">Paid upfront · in local currency</div>
            </div>
            <div className="bk-sb__fee-right">
              {computedGovFee === 0 ? (
                <span className="bk-sb__fee-amount bk-sb__fee-amount--green">FREE</span>
              ) : (
                <GovFeeTooltip
                  inrAmount={computedGovFee}
                  visaId={visaId}
                  govFeeIncreasePct={data.govFeeIncreasePct ?? 0}
                  govFeePageLink={data.govFeePageLink ?? null}
                />
              )}
            </div>
          </div>

          {/* Service fee */}
          <div className="bk-sb__fee-row">
            <div className="bk-sb__fee-col">
              <div className="bk-sb__fee-name-row">
                <div className="bk-sb__fee-name">
                  Service Fee <BkFeeInfoTooltip svcFee={computedSvcFee} />
                </div>
              </div>
            </div>
            <div className="bk-sb__fee-right">
              <span className="bk-sb__fee-amounts">
                <span className="bk-sb__fee-amount bk-sb__fee-amount--strike">
                  ₹{computedSvcOrig.toLocaleString("en-IN")}
                </span>
                <span className="bk-sb__fee-amount">₹{computedSvcFee.toLocaleString("en-IN")}</span>
              </span>
            </div>
          </div>

          {/* Express Processing — always visible toggle */}
          <div className="bk-sb__fee-row bk-sb__fee-row--express">
            <div className="bk-sb__fee-col">
              <div className="bk-sb__fee-name bk-sb__fee-name--row">
                <span>Express Processing</span>
                <button
                  className={`bk-express-toggle${expressVisa ? " bk-express-toggle--on" : ""}`}
                  onClick={onToggleExpress}
                  aria-label="Toggle express processing"
                  title={expressVisa ? "Disable express processing" : "Enable express processing"}
                >
                  <span className="bk-express-thumb" />
                </button>
              </div>
              <div className="bk-sb__fee-note">Priority handling — faster turnaround</div>
            </div>
            <div className="bk-sb__fee-right">
              <span className={`bk-sb__fee-amount${expressVisa ? " bk-sb__fee-amount--active" : " bk-sb__fee-amount--inactive"}`}>
                +₹{(EXPRESS_FEE ?? 1500).toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          <div className="bk-sb__divider" />

          {/* Total */}
          <div className="bk-sb__fee-row bk-sb__fee-row--total">
            <div className="bk-sb__fee-col">
              <div className="bk-sb__fee-name bk-sb__fee-name--bold">Total</div>
            </div>
            <span className="bk-sb__fee-amount bk-sb__fee-amount--total">
              ₹{computedTotal.toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        {/* Refund badge */}
        {refundPolicy && (
          <RefundBadge refundPolicy={refundPolicy} />
        )}

        <div className="bk-sb__trust">
          {["Secure", "Verified", "Trusted"].map((t) => (
            <div key={t} className="bk-sb__trust-item"><span>{t}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SCROLL HELPER ────────────────────────────────────────────────────────
// Measures the actual sticky nav height at call time and offsets accordingly.
// An extra 16px breathing room is added so the section head isn't flush with the nav.
function scrollToSection(el) {
  if (!el) return;
  const nav = document.querySelector(".bk-nav");
  const navH = nav ? nav.getBoundingClientRect().height : 0;
  const BUFFER = 16; // px of breathing room below the nav
  const top = el.getBoundingClientRect().top + window.scrollY - navH - BUFFER;
  window.scrollTo({ top, behavior: "smooth" });
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────
export default function BookingPage({ visaId: propVisaId }) {
  const { visaId: paramVisaId } = useParams();
  const visaId = paramVisaId || propVisaId || "uae";
  const fallbackData = useRef(getVisaData(visaId));
  const [data, setData] = useState(() => getVisaData(visaId));

  // Fetch visa product from API on mount / visaId change
  useEffect(() => {
    const fb = getVisaData(visaId);
    fallbackData.current = fb;
    setData(fb);
    VisaService.getProductBySlug(visaId).then((r) => {
      const ap = r?.data;
      if (!ap) return;
      setData((prev) => ({
        ...prev,
        country:           ap.country_name        ?? prev.country,
        flag:              ap.country_flag         ?? prev.flag,
        approvalRate:      ap.approval_rate        ?? prev.approvalRate,
        govFeeIncreasePct: ap.gov_fee_increase_pct ?? prev.govFeeIncreasePct,
        govFeePageLink:    ap.gov_fee_page_link    ?? prev.govFeePageLink,
        visaCard: {
          ...prev.visaCard,
          type:       ap.visa_type_label ?? prev.visaCard.type,
          stay:       ap.stay_duration   ?? prev.visaCard.stay,
          validity:   ap.validity        ?? prev.visaCard.validity,
          entry:      ap.entry_type      ?? prev.visaCard.entry,
          govFee:     ap.gov_fee         ?? prev.visaCard.govFee,
          svcFee:     ap.svc_fee         ?? prev.visaCard.svcFee,
          svcOrig:    ap.svc_fee_orig    ?? prev.visaCard.svcOrig,
          guarantee:  ap.guarantee       ?? prev.visaCard.guarantee,
          expressFee: ap.express_fee     ?? prev.visaCard.expressFee,
          badge:      ap.badge           ?? prev.visaCard.badge,
        },
        ...(Array.isArray(ap.visa_types)    && ap.visa_types.length    && { visaTypes:    ap.visa_types }),
        ...(Array.isArray(ap.essentials)    && ap.essentials.length    && { essentials:   ap.essentials }),
        ...(Array.isArray(ap.journey_steps) && ap.journey_steps.length && { journeySteps: ap.journey_steps }),
        ...(Array.isArray(ap.reviews)       && ap.reviews.length       && { reviews:      ap.reviews }),
        ...(Array.isArray(ap.faqs)          && ap.faqs.length          && { faqs:         ap.faqs }),
      }));
    }).catch(() => {});
  }, [visaId]);

  const navigate = useNavigate();

  const [heroLoaded, setHeroLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState("Info");
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [reviewSearch, setReviewSearch] = useState("");
  const [reviewFilter, setReviewFilter] = useState(0);
  const [faqSearch, setFaqSearch] = useState("");
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const [faqsExpanded, setFaqsExpanded] = useState(false);

  const { gateOpen, loggedInUser, requireLogin, handleLoginSuccess, closeGate } = useLoginGate();

  const visaTypes = data.visaTypes ?? [];
  const [selectedType, setSelectedType] = useState(visaTypes[0] ?? null);
  const [expressVisa, setExpressVisa] = useState(false);

  // Reset when country changes
  useEffect(() => {
    const types = data.visaTypes ?? [];
    setSelectedType(types[0] ?? null);
    setExpressVisa(false);
  }, [visaId]);

  // ── Computed fees
  const EXPRESS_FEE  = data.visaCard.expressFee ?? 1500;
  const govMult = selectedType?.govFeeMultiplier ?? 1;
  const svcMult = selectedType?.svcFeeMultiplier ?? 1;
  const govFeeIncreasePct = data.govFeeIncreasePct ?? 0;
  const computedGovFeeBase = Math.round(data.visaCard.govFee * govMult);
  const computedGovFee  = Math.round(computedGovFeeBase * (1 + govFeeIncreasePct / 100));
  const computedSvcFee  = Math.round(data.visaCard.svcFee  * svcMult);
  const computedSvcOrig = Math.round(data.visaCard.svcOrig * svcMult);
  const computedExpressFee  = expressVisa ? EXPRESS_FEE : 0;
  const computedTotal   = computedGovFee + computedSvcFee + computedExpressFee;

  const sectionRefs = useRef({});

  const filteredReviews = data.reviews.filter((r) => {
    const matchStar = reviewFilter === 0 || r.rating === reviewFilter;
    const q = reviewSearch.toLowerCase();
    const matchSearch = !q || r.name.toLowerCase().includes(q) || r.title.toLowerCase().includes(q) || r.body.toLowerCase().includes(q);
    return matchStar && matchSearch;
  });

  const filteredFaqs = data.faqs.filter((f) => {
    const q = faqSearch.toLowerCase();
    return !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
  });

  const isReviewFiltered = reviewSearch || reviewFilter !== 0;
  const isFaqFiltered = !!faqSearch;
  const visibleReviews = isReviewFiltered ? filteredReviews : (reviewsExpanded ? filteredReviews : filteredReviews.slice(0, REVIEWS_INITIAL));
  const visibleFaqs = isFaqFiltered ? filteredFaqs : (faqsExpanded ? filteredFaqs : filteredFaqs.slice(0, FAQS_INITIAL));
  const showReviewToggle = !isReviewFiltered && filteredReviews.length > REVIEWS_INITIAL;
  const showFaqToggle = !isFaqFiltered && filteredFaqs.length > FAQS_INITIAL;

  const registerRef = useCallback((id) => (el) => { if (el) sectionRefs.current[id] = el; }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.dataset.section); }); },
      { threshold: 0.35 }
    );
    Object.values(sectionRefs.current).forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [data]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".bk-reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [data]);

  const scrollTo = (sec) => {
    const el = sectionRefs.current[sec];
    if (!el) return;
    const navHeight = document.querySelector(".nav")?.offsetHeight ?? 80;
    const y = el.getBoundingClientRect().top + window.scrollY - navHeight - 20;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const handleApply = useCallback(() => {
    requireLogin(async (user) => {
      try {
        await saveApplication(user.email, visaId, {
          country:       data.country,
          flag:          data.flag,
          visaType:      selectedType?.label ?? data.visaCard.type,
          govFee:        computedGovFee,
          svcFee:        computedSvcFee,
          totalFee:      computedTotal,
          guarantee:     data.visaCard.guarantee,
          visaStay:      selectedType?.stay     ?? data.visaCard.stay,
          visaValidity:  selectedType?.validity  ?? data.visaCard.validity,
          visaEntry:     selectedType?.entry     ?? data.visaCard.entry,
          expressVisa:   expressVisa,
          expressFee:    computedExpressFee,
        });
      } catch (e) {
        console.error("Failed to save application", e);
      }
      navigate("/Profile");
    });
  }, [
    requireLogin, navigate, visaId, data,
    selectedType, computedGovFee, computedSvcFee,
    computedTotal, expressVisa, computedExpressFee,
  ]);

  const handleRewardsApply = useCallback(() => {
    setRewardsOpen(false);
    handleApply();
  }, [handleApply]);

  const handleDownloadPdf = useCallback(() => {
    downloadBookingPdf(
      data, selectedType,
      computedGovFee, computedSvcFee,
      0, computedTotal,
      expressVisa, computedExpressFee
    );
  }, [data, selectedType, computedGovFee, computedSvcFee, computedTotal, expressVisa, computedExpressFee]);

  useEffect(() => {
    window.AOS?.refreshHard();
  }, [activeSection, gateOpen, rewardsOpen]);

  // Derived info card values — update automatically with selected type
  const displayType     = selectedType?.label    ?? data.visaCard.type;
  const displayValidity = selectedType?.validity  ?? data.visaCard.validity;
  const displayEntry    = selectedType?.entry     ?? data.visaCard.entry;
  const displayStay     = selectedType?.stay      ?? data.visaCard.stay;

  return (
    <div className="bk">

      <LoginGate open={gateOpen} onClose={closeGate} onSuccess={handleLoginSuccess} />

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="bk-hero" data-aos="fade-left">
        <div className="bk-hero__bg">
          <img src={data.heroImg} alt={data.heroAlt} className={`bk-hero__img ${heroLoaded ? "loaded" : ""}`} onLoad={() => setHeroLoaded(true)} />
          <div className="bk-hero__overlay" />
          <div className="bk-hero__grain" />
        </div>
        <div className="bk-hero__content" data-aos="fade-left" data-aos-delay="80">
          <div className="bk-hero__breadcrumb">
            {data.breadcrumb.map((b, i) => (
              <span key={b}>
                {i > 0 && <span className="bk-hero__bc-sep bk-text-white"> / </span>}
                <span className={i === data.breadcrumb.length - 1 ? "bk-hero__bc-last bk-text-white" : "bk-hero__bc-item bk-text-white"}>{b}</span>
              </span>
            ))}
          </div>
          <div className="bk-hero__eyebrow"><span className="bk-text-white">{visaCard_eyebrow(data)}</span></div>
          <h1 className="bk-hero__title">{data.country}</h1>
          {/* <p className="bk-hero__tagline">{data.tagline}</p> */}
          <div className="bk-hero__cta-row">
            <button className="bk-hero__btn-start" onClick={handleApply}>
              <span className="bk-text-white">Start Application</span>
            </button>
            <button className="bk-hero__btn-learn" onClick={() => scrollTo("Info")}>View Details</button>
          </div>
        </div>

        <div className="bk-hero__badge">
          <div>
            <div className="bk-hero__badge-rate">
              <span className="bk-hero__badge-rate-label"> Approval Rate</span>
              <span className="bk-hero__badge-rate-num">{data.approvalRate ?? 95}%</span>
            </div>
            <div className="bk-hero__badge-title">{data.visaCard.guarantee}</div>
          </div>
        </div>
      </section>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav className="bk-nav" data-aos="fade-left" data-aos-delay="120">
        <div className="bk-nav__inner">
          {data.navLinks.map((sec) => (
            <button key={sec} className={`bk-nav__btn ${activeSection === sec ? "active" : ""}`} onClick={() => scrollTo(sec)}>{sec}</button>
          ))}
          <button className="bk-nav__apply " onClick={handleApply}>
            <span className="bk-text-white">Apply Now</span>
          </button>
        </div>
      </nav>

      {/* ── MAIN LAYOUT ──────────────────────────────────────────────── */}
      <div className="bk-layout" data-aos="fade-left" data-aos-delay="160">
        <main className="bk-main">

          {/* ── INFO ─────────────────────────────────────────────────── */}
          <section className="bk-section" data-section="Info" ref={registerRef("Info")} data-aos="fade-left">
            <div className="bk-reveal">
              <SectionHead label="Visa Info" title={`${data.country} Visa Details`} />

              {/* ── 4 Info Cards — Visa Type card has inline dropdown ── */}
              <div className="bk-info-grid">

                {/* Card 1: Visa Type — with dropdown */}
                <div className="bk-info-card bk-info-card--interactive">
                  <div className="bk-info-card__label-row">
                    <div className="bk-info-card__label">Visa Type</div>
                    {visaTypes.length > 1 && (
                      <span className="bk-info-card__change-hint">tap to change</span>
                    )}
                  </div>
                  <VisaTypeCardDropdown
                    visaTypes={visaTypes}
                    selectedType={selectedType}
                    onChange={(t) => { setSelectedType(t); }}
                  />
                </div>

                {/* Card 2: Stay Duration — auto-updates */}
                <div className="bk-info-card">
                  <div className="bk-info-card__label">Stay Duration</div>
                  <div className="bk-info-card__val bk-info-card__val--animated" key={displayStay}>{displayStay}</div>
                </div>

                {/* Card 3: Validity — auto-updates */}
                <div className="bk-info-card">
                  <div className="bk-info-card__label">Validity</div>
                  <div className="bk-info-card__val bk-info-card__val--animated" key={displayValidity}>{displayValidity}</div>
                </div>

                {/* Card 4: Entry Type — auto-updates */}
                <div className="bk-info-card">
                  <div className="bk-info-card__label">Entry Type</div>
                  <div className="bk-info-card__val bk-info-card__val--animated" key={displayEntry}>{displayEntry}</div>
                </div>

              </div>

              {/* Processing guarantee strip */}
              <div className="bk-info-guarantee-strip">
                <span className="bk-info-guarantee-strip__icon">⚡</span>
                <span className="bk-info-guarantee-strip__text">{data.visaCard.guarantee}</span>
                <span className="bk-info-guarantee-strip__rate">{data.approvalRate ?? 95}% Approval Rate</span>
              </div>
            </div>
          </section>

          {/* ── JOURNEY ──────────────────────────────────────────────── */}
         <section
            className="bk-section bk-section--no-border"
            data-section="Journey"
            ref={registerRef("Journey")}
            data-aos="fade-left"
            className="bk-section bk-section--no-border bk-section--journey"
          >
            <div className="bk-reveal">
              <SectionHead label="Step-by-Step Process" title={`How the ${data.country} Visa Works`} />
              <div className="bk-journey">
                {data.journeySteps.map((s) => (
                  <div key={s.step} className="bk-journey__step">
                    <div className="bk-journey__num">{s.step}</div>
                    <div className="bk-journey__body">
                      <div className="bk-journey__title">{s.title}</div>
                      <div className="bk-journey__desc">{s.desc}</div>
                      {s.tag && <div className="bk-journey__tag">{s.tag}</div>}
                    </div>
                  </div>
                ))}

                {/* Refund Policy step */}
                {(() => {
                  const refundStep = buildRefundStepDesc(visaId, data.refundPolicy);
                  if (!refundStep) return null;
                  return (
                    <div className="bk-journey__step bk-journey__step--refund">
                      <div className="bk-journey__num bk-journey__num--shield">🛡️</div>
                      <div className="bk-journey__body">
                        <div className="bk-journey__title">Refund &amp; Protection Policy</div>
                        <div className="bk-journey__refund-bullets">
                          {refundStep.bullets.map((b, i) => (
                            <div key={i} className="bk-journey__refund-bullet">
                              <span className="bk-journey__refund-icon">{b.icon}</span>
                              <span className="bk-journey__refund-text">{b.text}</span>
                            </div>
                          ))}
                        </div>
                        {refundStep.note && (
                          <div className="bk-journey__refund-note">{refundStep.note}</div>
                        )}
                        <div className="bk-journey__tag">TheVisa Protect</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </section>
          

    

  
          </main>
  
          <aside className="bk-sidebar-wrap">
            <Sidebar
              data={data}
              visaId={visaId}
              onApply={handleApply}
              selectedType={selectedType}
              computedGovFee={computedGovFee}
              computedSvcFee={computedSvcFee}
              computedSvcOrig={computedSvcOrig}
              computedTotal={computedTotal}
              expressVisa={expressVisa}
              onToggleExpress={() => setExpressVisa((v) => !v)}
              computedExpressFee={computedExpressFee}
              EXPRESS_FEE={EXPRESS_FEE}
            />
          </aside>
        </div>
  
        <div className="bk-layout_2">
          {/* ── ESSENTIALS ───────────────────────────────────────────── */}
          <section className="bk-section" data-section="Essentials" ref={registerRef("Essential")} data-aos="fade-left">
            <div className="bk-reveal">
              <SectionHead label="Required Documents" title="What You'll Need" />
              <div className="bk-essentials">
                <div className="bk-ess-card">
                  <div className="bk-ess-card__visual">
                    <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg" className="bk-ess-svg">
                      <rect x="20" y="10" width="180" height="120" rx="10" fill="#1a2744" />
                      <rect x="20" y="10" width="180" height="30" rx="10" fill="#243260" />
                      <rect x="20" y="30" width="180" height="10" fill="#243260" />
                      <circle cx="110" cy="55" r="16" fill="none" stroke="#c9a84c" strokeWidth="1.2" />
                      <circle cx="110" cy="55" r="11" fill="none" stroke="#c9a84c" strokeWidth="0.7" />
                      <ellipse cx="110" cy="55" rx="6" ry="16" fill="none" stroke="#c9a84c" strokeWidth="0.7" />
                      <line x1="94" y1="55" x2="126" y2="55" stroke="#c9a84c" strokeWidth="0.7" />
                      <line x1="96" y1="48" x2="124" y2="48" stroke="#c9a84c" strokeWidth="0.6" />
                      <line x1="96" y1="62" x2="124" y2="62" stroke="#c9a84c" strokeWidth="0.6" />
                      <rect x="36" y="80" width="64" height="8" rx="2" fill="#c9a84c" opacity="0.15" />
                      <rect x="36" y="92" width="44" height="6" rx="2" fill="#c9a84c" opacity="0.1" />
                      <rect x="120" y="80" width="64" height="5" rx="1.5" fill="#8899bb" opacity="0.25" />
                      <rect x="120" y="89" width="50" height="5" rx="1.5" fill="#8899bb" opacity="0.2" />
                      <rect x="120" y="98" width="56" height="5" rx="1.5" fill="#8899bb" opacity="0.2" />
                      <text x="110" y="27" textAnchor="middle" fill="#c9a84c" fontSize="8" fontFamily="serif" letterSpacing="2" opacity="0.9">PASSPORT</text>
                      <rect x="28" y="112" width="164" height="3" rx="1" fill="#8899bb" opacity="0.18" />
                      <rect x="28" y="118" width="164" height="3" rx="1" fill="#8899bb" opacity="0.13" />
                    </svg>
                  </div>
                  <div className="bk-ess-card__body"><div className="bk-ess-card__title">Passport</div></div>
                </div>
                <div className="bk-ess-card">
                  <div className="bk-ess-card__visual">
                    <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg" className="bk-ess-svg">
                      <rect x="10" y="18" width="200" height="104" rx="10" fill="#0d1e4a" />
                      <rect x="10" y="18" width="200" height="26" rx="10" fill="#c9a84c" />
                      <rect x="10" y="34" width="200" height="10" fill="#c9a84c" />
                      <text x="20" y="31" fill="#0d1e4a" fontSize="7.5" fontFamily="monospace" fontWeight="700" letterSpacing="1.5">AIR INDIA</text>
                      <text x="200" y="31" textAnchor="end" fill="#0d1e4a" fontSize="6.5" fontFamily="monospace" fontWeight="700" opacity="0.75">AI — 117</text>
                      <text x="20" y="64" fill="#c9a84c" fontSize="20" fontFamily="monospace" fontWeight="700" letterSpacing="-1">DEL</text>
                      <text x="20" y="73" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace" letterSpacing="0.5">NEW DELHI</text>
                      <line x1="42" y1="63" x2="110" y2="63" stroke="#c9a84c" strokeWidth="0.6" opacity="0.5" />
                      <text x="113" y="64" fill="#c9a84c" fontSize="20" fontFamily="monospace" fontWeight="700" letterSpacing="-1">CDG</text>
                      <text x="113" y="73" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace" letterSpacing="0.5">PARIS</text>
                      <rect x="17" y="78" width="132" height="14" rx="3" fill="#162455" />
                      <text x="22" y="87" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace">DATE</text>
                      <text x="46" y="87" fill="white" fontSize="6" fontFamily="monospace" fontWeight="700">14 MAR 2025</text>
                      <text x="94" y="87" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace">DEP</text>
                      <text x="110" y="87" fill="white" fontSize="6" fontFamily="monospace" fontWeight="700">06:40</text>
                      <text x="22" y="100" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace">CLASS</text>
                      <text x="46" y="100" fill="white" fontSize="6" fontFamily="monospace" fontWeight="700">ECONOMY</text>
                      <text x="94" y="100" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace">PAX</text>
                      <text x="110" y="100" fill="white" fontSize="6" fontFamily="monospace" fontWeight="700">01</text>
                      {[0, 2, 4.5, 6.5, 9, 11, 14, 15.5, 18, 20.5, 23, 25, 27.5, 30, 32, 34.5, 37, 39, 41.5].map((x, i) => (
                        <rect key={i} x={17 + x} y="108" width={i % 4 === 0 ? 1.8 : i % 3 === 0 ? 1.3 : 0.9} height="10" rx="0.2" fill="white" opacity={i % 5 === 0 ? 0.7 : 0.4} />
                      ))}
                      <text x="164" y="62" textAnchor="middle" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace" letterSpacing="0.5">SEAT</text>
                      <text x="164" y="76" textAnchor="middle" fill="white" fontSize="16" fontFamily="monospace" fontWeight="700">14A</text>
                      <text x="164" y="87" textAnchor="middle" fill="#8fa0c0" fontSize="5.5" fontFamily="monospace">GATE</text>
                      <text x="164" y="97" textAnchor="middle" fill="#c9a84c" fontSize="9" fontFamily="monospace" fontWeight="700">C12</text>
                      <text x="164" y="108" textAnchor="middle" fill="#8fa0c0" fontSize="5" fontFamily="monospace">BOARDING</text>
                      <text x="164" y="116" textAnchor="middle" fill="white" fontSize="6" fontFamily="monospace" fontWeight="700">05:50</text>
                    </svg>
                  </div>
                  <div className="bk-ess-card__body"><div className="bk-ess-card__title">Return Ticket</div></div>
                </div>
                <div className="bk-ess-card">
                  <div className="bk-ess-card__visual">
                    <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg" className="bk-ess-svg">
                      <rect x="20" y="30" width="180" height="104" rx="4" fill="#fafaf7" stroke="#e0dbd0" strokeWidth="0.8" />
                      <rect x="20" y="20" width="180" height="22" rx="4" fill="#1a2744" />
                      <rect x="20" y="40" width="144" height="10" fill="#1a2744" />
                      <text x="40" y="38" fill="#c9a84c" fontSize="7.5" fontFamily="monospace" fontWeight="700" letterSpacing="1">BANK STATEMENT</text>
                      <rect x="127" y="40" width="72" height="20" rx="3" fill="#f0fdf4" stroke="#16a34a" strokeWidth="0.8" />
                      <text x="146" y="48" textAnchor="middle" fill="#15803d" fontSize="6" fontFamily="monospace">BALANCE</text>
                      <text x="137" y="57" textAnchor="middle" fill="#15803d" fontSize="9" fontFamily="monospace" fontWeight="700">₹</text>
                      <text x="148" y="58.5" textAnchor="middle" fill="#15803d" fontSize="9" fontFamily="monospace" fontWeight="700">***</text>
                      {[
                        { label: "Jun 01  Credit Salary", amt: "****", color: "#15803d" },
                        { label: "Jun 15  UPI Transfer", amt: "-****", color: "#dc2626" },
                        { label: "Jul 01  Credit Salary", amt: "+****", color: "#15803d" },
                        { label: "Jul 22  Shopping", amt: "-****", color: "#dc2626" },
                      ].map((row, i) => (
                        <g key={i}>
                          <rect x="42" y={69 + i * 13} width="132" height="11" rx="1.5" fill={i % 2 === 0 ? "#f5f5f0" : "transparent"} />
                          <text x="46" y={75 + i * 13} fill="#555" fontSize="6" fontFamily="monospace">{row.label}</text>
                          <text x="170" y={75 + i * 13} textAnchor="end" fill={row.color} fontSize="6.5" fontFamily="monospace" fontWeight="700">{row.amt}</text>
                        </g>
                      ))}
                      <line x1="42" y1="124" x2="170" y2="124" stroke="#e0dbd0" strokeWidth="0.8" />
                    </svg>
                  </div>
                  <div className="bk-ess-card__body"><div className="bk-ess-card__title">Bank Statement</div></div>
                </div>
                <div className="bk-ess-card">
                  <div className="bk-ess-card__visual">
                    <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg" className="bk-ess-svg">
                      <path d="M110 18 L152 34 L152 76 C152 100 134 118 110 126 C86 118 68 100 68 76 L68 34 Z" fill="#1a2744" />
                      <path d="M110 24 L146 38 L146 76 C146 97 130 113 110 120 C90 113 74 97 74 76 L74 38 Z" fill="#243260" />
                      <path d="M110 30 L140 42 L140 76 C140 94 126 108 110 114 C94 108 80 94 80 76 L80 42 Z" fill="none" stroke="#c9a84c" strokeWidth="0.8" opacity="0.5" />
                      <path d="M96 72 L106 82 L126 60" stroke="#c9a84c" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                      <rect x="138" y="18" width="62" height="26" rx="5" fill="#0f2557" stroke="#c9a84c" strokeWidth="0.8" />
                      <text x="169" y="28" textAnchor="middle" fill="#c9a84c" fontSize="6" fontFamily="monospace" letterSpacing="0.5">COVERAGE</text>
                      <text x="169" y="38" textAnchor="middle" fill="white" fontSize="10" fontFamily="monospace" fontWeight="700">****</text>
                      <ellipse cx="110" cy="130" rx="30" ry="5" fill="#1a2744" opacity="0.15" />
                    </svg>
                  </div>
                  <div className="bk-ess-card__body"><div className="bk-ess-card__title">Travel Insurance</div></div>
                </div>
              </div>
            </div>
          </section>

          {/* ── PROCESS ──────────────────────────────────────────────── */}
          <section className="bk-section" data-section="Process" ref={registerRef("Process")}>
            <div className="bk-reveal">
              <SectionHead label="How It Works" title="4-Step Process With Us" />
              <RunwayProcess steps={data.process} />
            </div>
          </section>
  
          {/* ── REVIEWS ──────────────────────────────────────────────── */}
          <section className="bk-section" data-section="Why Us" ref={registerRef("Why Us")}>
            <div className="bk-reveal">
              <SectionHead label="Customer Reviews" title="What Our Customers Say" />
              <div className="bk-reviews-toolbar">
                <div className="bk-reviews-score">
                  <div className="bk-reviews-score__num">{data.reviewScore}</div>
                  <div>
                    <div className="bk-reviews-score__stars">★★★★★</div>
                    <div className="bk-reviews-score__count">{data.reviewCount} verified reviews</div>
                  </div>
                </div>
                <div className="bk-reviews-controls">
                  <SearchBar value={reviewSearch} onChange={setReviewSearch} placeholder="Search reviews…" />
                  <div className="bk-rev-filters">
                    {[0, 5, 4, 3].map((star) => (
                      <button key={star} className={`bk-rev-filter${reviewFilter === star ? " active" : ""}`} onClick={() => setReviewFilter(reviewFilter === star ? 0 : star)}>
                        {star === 0 ? "All" : `${star}★`}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bk-reviews-grid">
                {visibleReviews.length > 0 ? visibleReviews.map((r) => (
                  <div key={r.name + r.date} className="bk-review">
                    <div className="bk-review__top">
                      <div className="bk-review__avatar" style={{ "--avatar-bg": avatarColor(r.name) }}>{r.name.charAt(0)}</div>
                      <div>
                        <div className="bk-review__name">{r.name}</div>
                        <div className="bk-review__date">{r.date}</div>
                      </div>
                      <div className="bk-review__stars"><Stars n={r.rating} /></div>
                    </div>
                    <div className="bk-review__title">"{r.title}"</div>
                    <div className="bk-review__body">{r.body}</div>
                  </div>
                )) : (
                  <div className="bk-reviews-empty">No reviews match your search.</div>
                )}
              </div>
              {showReviewToggle && (
                <ViewMoreBtn expanded={reviewsExpanded} onToggle={() => setReviewsExpanded((v) => !v)} moreCount={filteredReviews.length - REVIEWS_INITIAL} />
              )}
            </div>
          </section>
  
          {/* ── FAQ ──────────────────────────────────────────────────── */}
          <section className="bk-section" data-section="FAQ" ref={registerRef("FAQ")}>
            <div className="bk-reveal">
              <SectionHead label={`${data.country} FAQ`} title="Frequently Asked Questions" />
              <div className="bk-faq-toolbar">
                <SearchBar value={faqSearch} onChange={setFaqSearch} placeholder="Search questions…" />
              </div>
              <div className="bk-faqs">
                {visibleFaqs.length > 0 ? visibleFaqs.map((f, idx) => (
                  <FaqItem
                    key={f.q}
                    q={f.q}
                    a={f.a}
                    isLast={idx === visibleFaqs.length - 1 && !showFaqToggle}
                  />
                )) : (
                  <div className="bk-reviews-empty bk-reviews-empty--padded">No questions match your search.</div>
                )}
              </div>
              {showFaqToggle && (
                <ViewMoreBtn expanded={faqsExpanded} onToggle={() => setFaqsExpanded((v) => !v)} moreCount={filteredFaqs.length - FAQS_INITIAL} />
              )}
  
              <div className="bk-faq-cta">
                <div className="bk-faq-cta__title">Ready to Visit {data.country}?</div>
                <div className="bk-faq-cta__sub">Join 50,000+ Indians who trust TheVisa for their travel documents.</div>
                <button className="bk-hero__btn-start" onClick={handleApply}>
                  <span className="bk-text-white">Apply for {data.country} Visa</span>
                </button>
              </div>
            </div>
          </section>
        </div>
  
        <FloatingToggle
          onApply={handleApply}
          onRewards={() => setRewardsOpen(true)}
          onDownloadPdf={handleDownloadPdf}
        />
  
        <RewardsModal
          open={rewardsOpen}
          onClose={() => setRewardsOpen(false)}
          onApply={handleRewardsApply}
        />
  
      </div>
    );
  }
  
  // ─── HELPERS ──────────────────────────────────────────────────────────────
  function visaCard_eyebrow(data) {
    const vc = data.visaCard;
    if (vc.govFee === 0) return `${vc.type} · FREE Govt Fee`;
    return `${vc.type} `;
  }
